import { TGetClaimedPrizeWinsResponse } from '@daymade/api-contracts';
import { MAIN_PRIZE_STRUCTURE_CODE } from '@daymade/common-lib/constants';
import { PrizeType } from '@daymade/common-lib/dbEnums';
import ImageWithFallback from 'components/ImageWithFallback';
import AccessGiftCardButton from 'components/dashboard/your-wins/AccessGiftCardButton';
import StarRatingSelector from 'components/my-wins/StarRatingSelector';
import { dashboardFolder } from 'constants/Dashboard';
import styles from './ClaimedPrize.module.css';
import {
  getDisplayPrizeTierCode,
  getPrizeTierIcon,
} from './prizeTierCodeImageMap';

interface ClaimedPrizeProps {
  className?: string;
  prizeWinId: string;
  prizeStructureCode: string;
  prizeTierCode: string;
  createdAt: Date | string;
  chosenAt: TGetClaimedPrizeWinsResponse[number]['chosenAt'];
  chosenPrizeName: TGetClaimedPrizeWinsResponse[number]['chosenPrizeName'];
  chosenPrizeType?: TGetClaimedPrizeWinsResponse[number]['chosenPrizeType'];
  chosenPrizeImageLocation: TGetClaimedPrizeWinsResponse[number]['chosenPrizeImageLocation'];
  chosenPrizeProducts?: TGetClaimedPrizeWinsResponse[number]['chosenPrizeProducts'];
  drawName?: string;
  showDrawInfo?: boolean;
  showPrizeFeedback?: boolean;
  feedbackRating: number | null;
  feedbackComment: string | null;
}

export const ClaimedPrize = ({
  className,
  prizeWinId,
  prizeStructureCode,
  prizeTierCode,
  createdAt,
  chosenAt,
  chosenPrizeName,
  chosenPrizeType,
  chosenPrizeImageLocation,
  chosenPrizeProducts,
  drawName,
  showDrawInfo,
  showPrizeFeedback,
  feedbackRating,
  feedbackComment,
}: ClaimedPrizeProps) => {
  const displayDate = chosenAt ?? createdAt;
  const selectedProductName =
    chosenPrizeType === PrizeType.CUSTOM
      ? (chosenPrizeProducts?.[0]?.name ?? chosenPrizeName)
      : chosenPrizeName;
  const shouldShowTierRow = prizeStructureCode === MAIN_PRIZE_STRUCTURE_CODE;
  const displayPrizeTierCode = getDisplayPrizeTierCode(prizeTierCode);
  const isTreat = /^Treat\d+$/i.test(prizeTierCode);

  const draw =
    drawName && /^Leaderboard Schedule/i.test(drawName)
      ? 'Leaderboard Raffle'
      : drawName
        ? drawName
        : '';

  return (
    <div
      className={
        className ? `${styles.container} ${className}` : styles.container
      }
    >
      <div className={styles.prizeImage}>
        <ImageWithFallback
          layout="fill"
          objectFit="cover"
          src={chosenPrizeImageLocation || ''}
          fallbackSrc={`${dashboardFolder}/FALLBACK_YOUR_WINS.png`}
        />
      </div>
      <div className={styles.textSection}>
        {selectedProductName ? (
          <h3
            className={`aboutUsTypographyHeaderMedium textAlignLeft noMargin ${styles.title}`}
          >
            {selectedProductName}
          </h3>
        ) : null}
        {shouldShowTierRow ? (
          <div className={styles.tierRow}>
            <div className={styles.tierIcon}>
              <ImageWithFallback
                layout="fill"
                src={getPrizeTierIcon(prizeTierCode)}
              />
            </div>
            <p className="aboutUsTypographyBodySmall noMargin">
              {displayPrizeTierCode}
              {isTreat ? '' : ' prize'}
            </p>
          </div>
        ) : null}
        <p className="aboutUsTypographyBodySmall noMargin">
          Claimed:{' '}
          {new Date(displayDate)
            .toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'short',
              year: '2-digit',
            })
            .replace(/(\w{3})\s(\d{2})/, '$1, $2')}
        </p>
        {showDrawInfo && drawName && (
          <p
            className={`${styles.drawName} aboutUsTypographyBodySmall noMargin`}
          >
            {draw}
            {createdAt
              ? `:${' '}
            ${new Date(createdAt)
              .toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: '2-digit',
              })
              .replace(/(\w{3})\s(\d{2})/, '$1, $2')}`
              : ''}
          </p>
        )}
        {chosenPrizeProducts &&
          chosenPrizeProducts.map(
            ({ autoFulfilmentCode, prizeOrderProductId }) => {
              switch (autoFulfilmentCode) {
                case 'GIFTCLOUD_REWARD':
                  return (
                    <AccessGiftCardButton
                      key={prizeOrderProductId}
                      prizeOrderProductId={prizeOrderProductId}
                    ></AccessGiftCardButton>
                  );
                default:
                  return null;
              }
            },
          )}
        {showPrizeFeedback && !isTreat && (
          <StarRatingSelector
            prizeWinId={prizeWinId}
            existingComment={feedbackComment}
            existingRating={feedbackRating}
          />
        )}
      </div>
    </div>
  );
};
